import {
  ReportEmpty,
  ReportError,
  ReportLoading,
  ReportMetricCard,
  ReportPageHeader,
  formatMoneyTotals,
  formatQuantity,
} from "@/components/reports/report-page";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { buildMetadata } from "@/lib/metadata";
import { ReportService } from "@/services/report-service";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Box, FileText, Package, ShoppingCart } from "lucide-react";
import { normalizeReportSearch, reportSearchSchema } from "./-report-search";

export const Route = createFileRoute("/_main/reports/product-mix")({
  validateSearch: reportSearchSchema,
  component: ProductMixRoute,
  head: () => ({
    meta: buildMetadata({
      title: "Product Mix Report",
      description: "Analyze supplier product demand and order composition",
      keywords: ["reports", "products", "demand"],
    }),
  }),
});

function ProductMixRoute() {
  const range = normalizeReportSearch(Route.useSearch());
  const navigate = Route.useNavigate();
  const reportQuery = useQuery({
    queryKey: ["reports", "product-mix", range.dateFrom, range.dateTo],
    queryFn: () => ReportService.getProductMix(range),
  });
  const report = reportQuery.data;

  return (
    <main className="space-y-7 p-4 md:p-8">
      <ReportPageHeader
        title="Product Mix Report"
        totalOrders={report?.order_count}
        range={range}
        onRangeChange={(nextRange) =>
          navigate({ search: nextRange, replace: true })
        }
      />

      {reportQuery.isLoading ? <ReportLoading /> : null}
      {reportQuery.isError ? <ReportError /> : null}

      {report ? (
        <>
          <section className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            <ReportMetricCard
              label="Unique Products"
              value={report.unique_products}
              icon={Box}
            />
            <ReportMetricCard
              label="Total Qty Ordered"
              value={formatQuantity(report.total_quantity)}
              icon={ShoppingCart}
            />
            <ReportMetricCard
              label="Top Product"
              value={report.top_product ?? "—"}
              icon={Package}
            />
            <ReportMetricCard
              label="Avg Items/Order"
              value={formatQuantity(report.average_items_per_order)}
              icon={FileText}
            />
          </section>

          <Card className="gap-0 overflow-hidden py-0 shadow-sm">
            <CardHeader className="py-5">
              <CardTitle className="font-medium text-base">
                Product Breakdown ({report.breakdown.length} products)
              </CardTitle>
            </CardHeader>
            {report.breakdown.length ? (
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Total Quantity</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead>UOM</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.breakdown.map((row) => (
                    <TableRow key={row.product_id}>
                      <TableCell>
                        <div className="font-medium">{row.product}</div>
                        <div className="text-muted-foreground text-xs">
                          {row.sku}
                        </div>
                      </TableCell>
                      <TableCell>
                        {formatQuantity(row.total_quantity)}
                      </TableCell>
                      <TableCell>{row.orders}</TableCell>
                      <TableCell>{row.uom ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <CardContent className="pb-6">
                <ReportEmpty>No matched products in this period.</ReportEmpty>
              </CardContent>
            )}
          </Card>

          <section className="space-y-4">
            <h2 className="font-medium text-base">Orders by period</h2>
            {report.orders.length ? (
              report.orders.map((order) => (
                <Card key={order.id} className="gap-4 py-5 shadow-sm">
                  <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">
                        #{order.order_number} · {order.customer}
                      </CardTitle>
                      <p className="mt-1 text-muted-foreground text-xs">
                        {order.date}
                      </p>
                    </div>
                    <Badge variant="outline">{order.status}</Badge>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead>Quantity</TableHead>
                          <TableHead>UOM</TableHead>
                          <TableHead className="text-right">Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {order.items.map((item, index) => (
                          <TableRow key={`${order.id}-${item.sku}-${index}`}>
                            <TableCell>{item.product}</TableCell>
                            <TableCell>
                              {formatQuantity(item.quantity)}
                            </TableCell>
                            <TableCell>{item.uom ?? "—"}</TableCell>
                            <TableCell className="text-right">
                              {item.amount != null
                                ? formatMoneyTotals([
                                    {
                                      amount: item.amount,
                                      currency: item.currency ?? "UNKNOWN",
                                    },
                                  ])
                                : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={3} className="font-medium">
                            Total
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatMoneyTotals(order.totals)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))
            ) : (
              <ReportEmpty>No product orders in this period.</ReportEmpty>
            )}
          </section>
        </>
      ) : null}
    </main>
  );
}
